/**
 * Lender Portal - Enhanced Registration Page
 * Multi-step registration with comprehensive verification for anti-scam protection
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import {
  Building2,
  Mail,
  Phone,
  Globe,
  FileText,
  Shield,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Banknote,
  Users,
  Lock,
  MapPin,
  Calendar,
  Upload,
  AlertTriangle,
  Info,
  FileCheck
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: 'Company Details', icon: Building2 },
  { id: 2, title: 'Verification', icon: Shield },
  { id: 3, title: 'Contact Info', icon: Mail },
  { id: 4, title: 'Credentials', icon: Lock },
  { id: 5, title: 'Review', icon: CheckCircle2 }
];

const LenderRegister = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: Company Details
    company_name: '',
    country: 'SL',
    provider_type: 'finance_company',
    description: '',
    years_in_business: '',
    
    // Step 2: Verification Documents
    business_registration: '',
    licence_number: '',
    tax_id: '',
    website_url: '',
    document_urls: [],
    
    // Step 3: Contact Info
    contact_person: '',
    email: '',
    phone: '',
    physical_address: '',
    
    // Step 4: Credentials
    password: '',
    confirm_password: ''
  });
  
  const [errors, setErrors] = useState({});

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.company_name) newErrors.company_name = 'Company name is required';
      if (!formData.country) newErrors.country = 'Country is required';
      if (!formData.provider_type) newErrors.provider_type = 'Provider type is required';
    }
    
    if (step === 2) {
      // At least one verification document is required
      if (!formData.business_registration && !formData.licence_number) {
        newErrors.verification = 'Please provide at least Business Registration OR Licence Number';
      }
    }
    
    if (step === 3) {
      if (!formData.contact_person) newErrors.contact_person = 'Contact person name is required';
      if (!formData.email) newErrors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
      if (!formData.phone) newErrors.phone = 'Phone is required';
      if (!formData.physical_address) newErrors.physical_address = 'Physical address is required for verification';
    }
    
    if (step === 4) {
      if (!formData.password) newErrors.password = 'Password is required';
      else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (formData.password !== formData.confirm_password) {
        newErrors.confirm_password = 'Passwords do not match';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!agreedToTerms) {
      toast({
        title: "Terms Required",
        description: "Please agree to the terms and conditions",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setSubmitting(true);
      
      const response = await axios.post(`${API_URL}/api/finance/lender/register`, {
        company_name: formData.company_name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        country: formData.country,
        contact_person: formData.contact_person,
        licence_number: formData.licence_number || null,
        business_registration: formData.business_registration || null,
        tax_id: formData.tax_id || null,
        website_url: formData.website_url || null,
        physical_address: formData.physical_address || null,
        years_in_business: formData.years_in_business ? parseInt(formData.years_in_business) : null,
        description: formData.description || null,
        document_urls: formData.document_urls || []
      });
      
      if (response.data.success) {
        const score = response.data.verification_score || 0;
        toast({
          title: "Registration Submitted!",
          description: `Your application (Score: ${score}/100) has been submitted for review. You'll receive an email within 24-48 hours.`
        });
        navigate('/lender/login', { state: { registered: true } });
      }
      
    } catch (error) {
      toast({
        title: "Registration Failed",
        description: error.response?.data?.detail || "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate verification score preview
  const getVerificationScorePreview = () => {
    let score = 0;
    
    if (formData.business_registration) score += 20;
    if (formData.licence_number) score += 20;
    if (formData.website_url) score += 10;
    
    // Contact checks
    const emailDomain = formData.email.split('@')[1] || '';
    const freeEmails = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
    if (emailDomain && !freeEmails.includes(emailDomain.toLowerCase())) score += 5;
    if (formData.phone) score += 5;
    if (formData.physical_address) score += 5;
    
    // Business history
    const years = parseInt(formData.years_in_business) || 0;
    if (years >= 10) score += 15;
    else if (years >= 5) score += 12;
    else if (years >= 2) score += 8;
    else if (years >= 1) score += 5;
    
    // Documents
    if (formData.document_urls.length >= 3) score += 15;
    else if (formData.document_urls.length >= 1) score += 5;
    
    return Math.min(score, 100);
  };

  const scorePreview = getVerificationScorePreview();
  const scoreColor = scorePreview >= 70 ? 'text-green-600' : scorePreview >= 40 ? 'text-yellow-600' : 'text-red-600';

  const renderStep1 = () => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="company_name">Company Name *</Label>
        <Input
          id="company_name"
          placeholder="Your registered company name"
          value={formData.company_name}
          onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
          className={errors.company_name ? 'border-red-500' : ''}
        />
        {errors.company_name && <p className="text-xs text-red-500 mt-1">{errors.company_name}</p>}
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Country *</Label>
          <Select
            value={formData.country}
            onValueChange={(v) => setFormData(prev => ({ ...prev, country: v }))}
          >
            <SelectTrigger className={errors.country ? 'border-red-500' : ''}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SL">🇱🇰 Sri Lanka</SelectItem>
              <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
              <SelectItem value="US">🇺🇸 United States</SelectItem>
              <SelectItem value="AU">🇦🇺 Australia</SelectItem>
              <SelectItem value="CA">🇨🇦 Canada</SelectItem>
              <SelectItem value="IE">🇮🇪 Ireland</SelectItem>
              <SelectItem value="NZ">🇳🇿 New Zealand</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label>Provider Type *</Label>
          <Select
            value={formData.provider_type}
            onValueChange={(v) => setFormData(prev => ({ ...prev, provider_type: v }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bank">Bank</SelectItem>
              <SelectItem value="finance_company">Finance Company</SelectItem>
              <SelectItem value="dealer_finance">Dealer Finance</SelectItem>
              <SelectItem value="credit_union">Credit Union</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label htmlFor="years_in_business">Years in Business</Label>
        <Select
          value={formData.years_in_business}
          onValueChange={(v) => setFormData(prev => ({ ...prev, years_in_business: v }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select years in business" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Less than 1 year</SelectItem>
            <SelectItem value="1">1-2 years</SelectItem>
            <SelectItem value="3">3-5 years</SelectItem>
            <SelectItem value="6">6-10 years</SelectItem>
            <SelectItem value="11">10+ years</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500 mt-1">Established businesses score higher in verification</p>
      </div>
      
      <div>
        <Label htmlFor="description">Company Description</Label>
        <Textarea
          id="description"
          placeholder="Briefly describe your finance services, target customers, and specializations..."
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={3}
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <Alert className="bg-blue-50 border-blue-200">
        <Shield className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Anti-Fraud Verification:</strong> We verify all lenders to protect buyers from scams. 
          Providing accurate information speeds up your approval.
        </AlertDescription>
      </Alert>
      
      {errors.verification && (
        <Alert className="bg-red-50 border-red-200">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{errors.verification}</AlertDescription>
        </Alert>
      )}
      
      <div>
        <Label htmlFor="business_registration">
          Business Registration Number *
          <Badge variant="outline" className="ml-2 text-xs">+20 points</Badge>
        </Label>
        <Input
          id="business_registration"
          placeholder="e.g., PV12345 (Sri Lanka) or 12345678 (UK)"
          value={formData.business_registration}
          onChange={(e) => setFormData(prev => ({ ...prev, business_registration: e.target.value }))}
        />
        <p className="text-xs text-gray-500 mt-1">Your official company registration number</p>
      </div>
      
      <div>
        <Label htmlFor="licence_number">
          Financial Licence/Regulatory Number
          <Badge variant="outline" className="ml-2 text-xs">+20 points</Badge>
        </Label>
        <Input
          id="licence_number"
          placeholder="Your financial regulatory licence number"
          value={formData.licence_number}
          onChange={(e) => setFormData(prev => ({ ...prev, licence_number: e.target.value }))}
        />
        <p className="text-xs text-gray-500 mt-1">Licence from Central Bank, FCA, or relevant authority</p>
      </div>
      
      <div>
        <Label htmlFor="tax_id">Tax ID / VAT Number</Label>
        <Input
          id="tax_id"
          placeholder="Your tax identification number"
          value={formData.tax_id}
          onChange={(e) => setFormData(prev => ({ ...prev, tax_id: e.target.value }))}
        />
      </div>
      
      <div>
        <Label htmlFor="website_url">
          Company Website
          <Badge variant="outline" className="ml-2 text-xs">+15 points</Badge>
        </Label>
        <Input
          id="website_url"
          placeholder="https://www.yourcompany.com"
          value={formData.website_url}
          onChange={(e) => setFormData(prev => ({ ...prev, website_url: e.target.value }))}
        />
        <p className="text-xs text-gray-500 mt-1">We'll verify your website is accessible</p>
      </div>
      
      <div className="bg-gray-50 p-4 rounded-lg border">
        <Label className="flex items-center gap-2 mb-3">
          <Upload className="h-4 w-4" />
          Upload Verification Documents
          <Badge variant="outline" className="text-xs">+15 points</Badge>
        </Label>
        <p className="text-sm text-gray-600 mb-3">
          Upload documents like: Business Registration Certificate, Financial Licence, Company Logo, Office Photos
        </p>
        <div className="flex flex-wrap gap-2">
          {formData.document_urls.map((url, idx) => (
            <Badge key={idx} variant="secondary" className="flex items-center gap-1">
              <FileCheck className="h-3 w-3" />
              Document {idx + 1}
              <button
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  document_urls: prev.document_urls.filter((_, i) => i !== idx)
                }))}
                className="ml-1 hover:text-red-500"
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Document upload feature coming soon. For now, you can provide document links via email after registration.
        </p>
      </div>
      
      {/* Verification Score Preview */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-lg border">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Verification Score Preview</span>
          <span className={`text-2xl font-bold ${scoreColor}`}>{scorePreview}/100</span>
        </div>
        <Progress value={scorePreview} className="h-2" />
        <p className="text-xs text-gray-500 mt-2">
          {scorePreview >= 70 ? '✅ Great! Your application looks complete.' :
           scorePreview >= 40 ? '⚠️ Add more details to improve your score.' :
           '❌ Please provide more verification information.'}
        </p>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="contact_person">Contact Person Name *</Label>
        <Input
          id="contact_person"
          placeholder="Full name of primary contact"
          value={formData.contact_person}
          onChange={(e) => setFormData(prev => ({ ...prev, contact_person: e.target.value }))}
          className={errors.contact_person ? 'border-red-500' : ''}
        />
        {errors.contact_person && <p className="text-xs text-red-500 mt-1">{errors.contact_person}</p>}
      </div>
      
      <div>
        <Label htmlFor="email">
          Business Email *
          <Badge variant="outline" className="ml-2 text-xs">Business email = +5 points</Badge>
        </Label>
        <Input
          id="email"
          type="email"
          placeholder="contact@yourcompany.com"
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
          className={errors.email ? 'border-red-500' : ''}
        />
        {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
        <p className="text-xs text-gray-500 mt-1">Use your company email domain for higher verification score</p>
      </div>
      
      <div>
        <Label htmlFor="phone">Phone Number *</Label>
        <Input
          id="phone"
          type="tel"
          placeholder="+94 77 123 4567"
          value={formData.phone}
          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
          className={errors.phone ? 'border-red-500' : ''}
        />
        {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
      </div>
      
      <div>
        <Label htmlFor="physical_address">
          Physical Office Address *
          <Badge variant="outline" className="ml-2 text-xs">Required for verification</Badge>
        </Label>
        <Textarea
          id="physical_address"
          placeholder="Full address of your main office (Street, City, Province/State, Postal Code)"
          value={formData.physical_address}
          onChange={(e) => setFormData(prev => ({ ...prev, physical_address: e.target.value }))}
          className={errors.physical_address ? 'border-red-500' : ''}
          rows={2}
        />
        {errors.physical_address && <p className="text-xs text-red-500 mt-1">{errors.physical_address}</p>}
        <p className="text-xs text-gray-500 mt-1">We may verify this address. P.O. Boxes are not accepted.</p>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4">
      <Alert className="bg-amber-50 border-amber-200">
        <Lock className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          Create a strong password. You'll use this to access your Lender Dashboard.
        </AlertDescription>
      </Alert>
      
      <div>
        <Label htmlFor="password">Password *</Label>
        <Input
          id="password"
          type="password"
          placeholder="Minimum 8 characters"
          value={formData.password}
          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
          className={errors.password ? 'border-red-500' : ''}
        />
        {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
      </div>
      
      <div>
        <Label htmlFor="confirm_password">Confirm Password *</Label>
        <Input
          id="confirm_password"
          type="password"
          placeholder="Re-enter password"
          value={formData.confirm_password}
          onChange={(e) => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
          className={errors.confirm_password ? 'border-red-500' : ''}
        />
        {errors.confirm_password && <p className="text-xs text-red-500 mt-1">{errors.confirm_password}</p>}
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-4">
      <Alert className="bg-green-50 border-green-200">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800">
          Please review your information before submitting. Our team will verify your details within 24-48 hours.
        </AlertDescription>
      </Alert>
      
      {/* Verification Score */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium text-blue-800">Your Verification Score</span>
          <span className={`text-3xl font-bold ${scoreColor}`}>{scorePreview}/100</span>
        </div>
        <Progress value={scorePreview} className="h-3" />
        <p className="text-sm text-blue-700 mt-2">
          {scorePreview >= 70 ? '✅ High score - faster approval likely' :
           scorePreview >= 40 ? '⚠️ Moderate score - may require additional verification' :
           '❌ Low score - please provide more information to improve chances'}
        </p>
      </div>
      
      <div className="space-y-3">
        <h4 className="font-semibold text-gray-700 flex items-center gap-2">
          <Building2 className="h-4 w-4" /> Company Details
        </h4>
        <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
          <p><span className="text-gray-500">Company:</span> <strong>{formData.company_name}</strong></p>
          <p><span className="text-gray-500">Country:</span> {formData.country}</p>
          <p><span className="text-gray-500">Type:</span> {formData.provider_type}</p>
          <p><span className="text-gray-500">Years in Business:</span> {formData.years_in_business || 'Not specified'}</p>
        </div>
        
        <h4 className="font-semibold text-gray-700 flex items-center gap-2 mt-4">
          <Shield className="h-4 w-4" /> Verification Documents
        </h4>
        <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
          <p><span className="text-gray-500">Business Registration:</span> {formData.business_registration || '❌ Not provided'}</p>
          <p><span className="text-gray-500">Licence Number:</span> {formData.licence_number || '❌ Not provided'}</p>
          <p><span className="text-gray-500">Tax ID:</span> {formData.tax_id || 'Not provided'}</p>
          <p><span className="text-gray-500">Website:</span> {formData.website_url || 'Not provided'}</p>
        </div>
        
        <h4 className="font-semibold text-gray-700 flex items-center gap-2 mt-4">
          <Mail className="h-4 w-4" /> Contact Information
        </h4>
        <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
          <p><span className="text-gray-500">Contact Person:</span> {formData.contact_person}</p>
          <p><span className="text-gray-500">Email:</span> {formData.email}</p>
          <p><span className="text-gray-500">Phone:</span> {formData.phone}</p>
          <p><span className="text-gray-500">Address:</span> {formData.physical_address}</p>
        </div>
      </div>
      
      <div className="flex items-start gap-2 pt-4 border-t">
        <Checkbox
          id="terms"
          checked={agreedToTerms}
          onCheckedChange={setAgreedToTerms}
        />
        <Label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer">
          I confirm that all information provided is accurate and I agree to the{' '}
          <a href="#" className="text-blue-600 hover:underline">Terms of Service</a> and{' '}
          <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a>.
          I understand that providing false information may result in rejection or account suspension.
        </Label>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Banknote className="h-8 w-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Lender Registration</h1>
          </div>
          <p className="text-gray-600">Join Zeilian's verified network of finance providers</p>
        </div>
        
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex justify-between">
            {STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isComplete = currentStep > step.id;
              
              return (
                <div key={step.id} className="flex flex-col items-center flex-1">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all
                    ${isComplete ? 'bg-green-500 text-white' : 
                      isActive ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}
                  `}>
                    {isComplete ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </div>
                  <span className={`text-xs text-center ${isActive ? 'text-blue-600 font-semibold' : 'text-gray-500'}`}>
                    {step.title}
                  </span>
                  {index < STEPS.length - 1 && (
                    <div className={`hidden sm:block absolute h-0.5 w-full top-5 left-1/2 -z-10
                      ${isComplete ? 'bg-green-500' : 'bg-gray-200'}`} 
                    />
                  )}
                </div>
              );
            })}
          </div>
          <Progress value={(currentStep / STEPS.length) * 100} className="mt-4 h-2" />
        </div>
        
        {/* Form Card */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>{STEPS[currentStep - 1].title}</CardTitle>
            <CardDescription>
              {currentStep === 1 && 'Tell us about your finance company'}
              {currentStep === 2 && 'Provide verification documents to prevent fraud'}
              {currentStep === 3 && 'How can we reach you?'}
              {currentStep === 4 && 'Set up your account credentials'}
              {currentStep === 5 && 'Review and submit your application'}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep5()}
          </CardContent>
          
          <CardFooter className="flex justify-between border-t pt-4">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            
            {currentStep < 5 ? (
              <Button onClick={handleNext}>
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={submitting || !agreedToTerms}
                className="bg-green-600 hover:bg-green-700"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
            )}
          </CardFooter>
        </Card>
        
        {/* Already registered link */}
        <p className="text-center mt-6 text-gray-600">
          Already registered?{' '}
          <Link to="/lender/login" className="text-blue-600 hover:underline font-medium">
            Sign in to your dashboard
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LenderRegister;
